import { AuthStore, UserApiService } from '@admin/core';
import { StatusEntity, UserChangePassword, UserEntity } from '@admin/core/models';
import { RoleApiService } from '@admin/core/services/role-api.service';
import { ValidatorMatch, ValidatorPassword } from '@admin/shared';
import { StatusValues } from '@admin/shared/data';
import { formControl, Validators } from '@al00x/forms';
import { Component, inject, signal } from '@angular/core';
import { ButtonClickEvent, CacBaseDialogComponent, CacButtonComponent, CacDialogLayoutComponent, CacFormBuilderComponent, formBuilder } from '@controladad/ng-base';
import { of, switchMap } from 'rxjs';

export type UserDialogData = UserEntity | undefined;

export type UserDialogResult = {
  isMe: boolean;
} | boolean;

@Component({
  selector: 'app-user-dialog',
  imports: [CacDialogLayoutComponent, CacFormBuilderComponent, CacButtonComponent],
  templateUrl: './user-dialog.component.html',
})
export class UserDialogComponent extends CacBaseDialogComponent<UserDialogData, UserDialogResult> {
  private readonly userApi = inject(UserApiService);
  private readonly roleApi = inject(RoleApiService);
  private readonly auth = inject(AuthStore);

  userForm = formBuilder({
    cols: 2,
    inputs: {
      firstName: {
        control: formControl<string>(undefined, Validators.required),
        label: 'نام',
      },
      lastName: {
        control: formControl<string>(undefined, Validators.required),
        label: 'نام خانوادگی',
      },
      username: {
        control: formControl<string>(undefined, Validators.required),
        label: 'کد پرسنلی',
      },
      status: {
        control: formControl<StatusEntity>(StatusEntity.Enabled, Validators.required),
        label: 'وضعیت',
        type: 'select',
        searchable: false,
        clearable: false,
        items: StatusValues,
      },
      roleIds: {
        control: formControl<number[]>(undefined, Validators.required),
        label: 'نقش',
        colspan: 2,
        type: 'select',
        searchable: false,
        clearable: false,
        items: this.roleApi.getRoleItems(),
        multiple: true,
      },
      password: {
        control: formControl<string>(undefined, [Validators.required, ValidatorPassword()]),
        label: 'پسورد',
        inputType: 'password-eye',
        autocomplete: false,
      },
      repeatPassword: {
        control: formControl<string>(undefined),
        label: 'تکرار پسورد',
        inputType: 'password-eye',
      },
    },
    onInit: (controls) => {
      controls.repeatPassword.setValidators([Validators.required, ValidatorMatch(controls.password)]);
    },
  });

  updatePasswordForm = formBuilder({
    cols: 2,
    inputs: {
      newPassword: {
        control: formControl<string>(undefined, [Validators.required, ValidatorPassword()]),
        label: 'پسورد جدید',
        inputType: 'password-eye',
      },
      repeatNewPassword: {
        control: formControl<string>(undefined, Validators.required),
        label: 'تکرار پسورد جدید',
        inputType: 'password-eye',
      },
    },
    onInit: (controls) => {
      controls.repeatNewPassword.setValidators([Validators.required, ValidatorMatch(controls.newPassword)]);
    },
  });

  changePasswordMode = signal(false);

  constructor() {
    super();
    const user = this.data;
    if (!user) return;
    this.userForm.patchValue({
      ...user,
      roleIds: user.roles.map(x => x.id),
    });
    this.userForm.setHidden(true, 'password', 'repeatPassword');
  }

  onToggleChangePassword() {
    const mode = this.changePasswordMode();
    this.changePasswordMode.set(!mode);
  }

  onSubmit(e: ButtonClickEvent) {
    let userValues = this.userForm.getValue();
    if (!userValues) return;
    const userId = this.data?.id;
    if (!userId) {
      const { repeatPassword, ...model } = userValues;
      this.userApi.createUser(model).pipe(e.pipe(),).subscribe(() => {
        this.submit(true);
      });
    } else {
      let passwordModel: UserChangePassword;
      if (this.changePasswordMode()) {
        const passwordValues = this.updatePasswordForm.getValue();
        if (!passwordValues) return;
        passwordModel = {
          newPassword: passwordValues.newPassword,
        };
      }
      const { password, repeatPassword, ...model } = userValues;
      const isMe = userId === this.auth.state.user?.id;
      this.userApi.updateUser(userId, model).pipe(
        switchMap(() => passwordModel ? this.userApi.changePassword(userId, passwordModel) : of(null)),
        switchMap(() => isMe ? this.auth.updateProfile() : of(null)),
        e.pipe(),
      ).subscribe(() => {
        this.submit({ isMe });
      });
    }
  }
}
